/**
 * saturn-pricing.js — Saturn Press Holiday pricing engine (portable core)
 *
 * Pure logic. No DOM, no theme assumptions. The single source of pricing truth
 * consumed by: the collection-grid card, the PDP display, and (later) the owner's
 * Personalization configurator. Each surface calls computePricing(config, qty)
 * and renders the result however it likes.
 *
 * Config is rendered from product metafields into a JSON island; this module reads it.
 * All money is in integer cents to avoid floating-point drift.
 *
 * ── Config shapes ───────────────────────────────────────────────────────────
 * Door A (Individual, block pricing):
 *   { model:"block_pricing", minQty:1, blockSize:8, blockPriceCents:3500, singleCents:700 }
 *
 * Door C (Bulk, whole-order volume tiers):
 *   { model:"volume_tiers", minQty:50, tiers:[{min,unitCents}, ...] }
 *   Optional: compareAtUnitCents — the single-card price (Door A) the island
 *   injects so the page can strike it through against the tier price.
 *
 * Door D (Personalization, volume tiers with the fee folded into the rate — 2026-09-10):
 *   { model:"volume_tiers", personalized:true, label:"Personalization pricing", minQty:50, tiers:[...] }
 *   `personalized:true` is what makes a tier door the Personalize door (ROADMAP #136);
 *   a legacy config still carrying setupFeeCents/unitSurchargeCents is read as Door D
 *   too and computes the old way (fee line + surcharge) until it is re-applied.
 *   (no compareAtUnitCents — personalization shows no "savings vs singles")
 *
 * Door B (Box, whole boxes — quantity is BOXES, not cards):
 *   { model:"box_multiple", minQty:1, blockSize:8, boxPriceCents:3500, singleCents:700 }
 *   total = qty x boxPriceCents; cards = qty x blockSize; the per-card rate is
 *   struck against singleCents to surface the box saving. Display == charge
 *   (it's just the native variant price x qty), so Door B needs no checkout Function.
 *
 * unitCount: a model may report how many *cards* the order is (out.unitCount) so
 * the per-unit figures (unitEffectiveCents, compareAtUnitCents) stay per-card even
 * when the input quantity is in boxes. Defaults to qty for card-quantity models.
 *
 * ── Result shape ────────────────────────────────────────────────────────────
 *   { valid, validationMessage, quantity, model, subtotalCents, unitEffectiveCents,
 *     compareAtSubtotalCents, compareAtUnitCents, savingsCents, discountPct,
 *     breakdown:[{label, amountCents}], nudge, minQty }
 *
 *   Block compare-at is intrinsic (qty x singleCents); tier compare-at appears
 *   only if compareAtUnitCents is supplied. discountPct is floored.
 *
 *   complete_block nudge: { type, cardsToAdd, nextBlockQty, costToCompleteCents,
 *     perAddedCardCents, isFree, savesMoney, message }. costToComplete is signed —
 *     negative means a full box is cheaper than the loose singles on hand.
 */
(function (root) {
  "use strict";

  function tierFor(tiers, qty) {
    // tiers sorted ascending by min; pick the highest tier whose min <= qty
    var chosen = null;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].min) chosen = tiers[i];
    }
    return chosen;
  }

  function nextTier(tiers, qty) {
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].min > qty) return tiers[i];
    }
    return null;
  }

  function computeBlock(cfg, qty) {
    var blocks = Math.floor(qty / cfg.blockSize);
    var remainder = qty % cfg.blockSize;
    var subtotal = blocks * cfg.blockPriceCents + remainder * cfg.singleCents;

    var breakdown = [];
    if (blocks > 0) {
      breakdown.push({
        label: blocks + " set" + (blocks > 1 ? "s" : "") + " of " + cfg.blockSize,
        amountCents: blocks * cfg.blockPriceCents
      });
    }
    if (remainder > 0) {
      breakdown.push({
        label: remainder + " single" + (remainder > 1 ? "s" : ""),
        amountCents: remainder * cfg.singleCents
      });
    }

    // Complete-the-box nudge. Carries the economics the display needs to frame
    // it per ladder position: a margin below breakeven, "free" at breakeven
    // (remainder*single === blockPrice), "cheaper" past it. The consumer renders
    // persona copy from these fields; `message` is only a plain fallback.
    var nudge = null;
    if (remainder > 0) {
      var toAdd = cfg.blockSize - remainder;
      // Cost to turn `remainder` loose singles into one full box: the box price
      // minus what those singles already cost. Negative past breakeven — the box
      // is cheaper than the loose singles already in the cart.
      var costToComplete = cfg.blockPriceCents - remainder * cfg.singleCents;
      nudge = {
        type: "complete_block",
        cardsToAdd: toAdd,
        nextBlockQty: qty + toAdd,
        costToCompleteCents: costToComplete,
        perAddedCardCents: toAdd > 0 ? Math.round(costToComplete / toAdd) : 0,
        isFree: costToComplete === 0,
        savesMoney: costToComplete < 0,
        message: "Add " + toAdd + " more to complete a set of " + cfg.blockSize + "."
      };
    }
    return {
      subtotal: subtotal,
      breakdown: breakdown,
      nudge: nudge,
      // Anchor for the strikethrough/discount: what the whole order would cost as
      // singles. Yields "$X less than singles" only once a box completes.
      compareAtSubtotalCents: qty * cfg.singleCents
    };
  }

  function computeTiers(cfg, qty) {
    var tier = tierFor(cfg.tiers, qty);
    var baseUnit = tier ? tier.unitCents : (cfg.tiers[0] ? cfg.tiers[0].unitCents : 0);
    var surcharge = cfg.unitSurchargeCents || 0;
    var setup = cfg.setupFeeCents || 0;
    var perCard = baseUnit + surcharge;
    var subtotal = qty * perCard + setup;

    var breakdown = [];
    if (setup > 0) breakdown.push({ label: "Setup fee", amountCents: setup });
    breakdown.push({
      label: qty + " cards \u00d7 " + fmt(perCard) + (surcharge ? " (" + fmt(baseUnit) + " + " + fmt(surcharge) + " personalization)" : ""),
      amountCents: qty * perCard
    });

    // Round-up nudge: if the next tier is close and lowers the per-card (and often the total)
    var nudge = null;
    var nt = nextTier(cfg.tiers, qty);
    if (nt) {
      var toAdd = nt.min - qty;
      var newPerCard = nt.unitCents + surcharge;
      var totalAtNext = nt.min * newPerCard + setup;
      nudge = {
        type: "next_tier",
        cardsToAdd: toAdd,
        nextUnitCents: nt.unitCents,
        totalAtNextCents: totalAtNext,
        savesMoney: totalAtNext < subtotal,
        message: "Add " + toAdd + " more (" + nt.min + " total) to drop to " + fmt(nt.unitCents) + "/card"
          + (totalAtNext < subtotal ? " and pay less overall." : ".")
      };
    }
    return {
      subtotal: subtotal,
      breakdown: breakdown,
      nudge: nudge,
      // Optional anchor: the island injects `compareAtUnitCents` (Door A's single
      // price) on the Bulk door so the page can strike through "$7 → $3.75 (46%)".
      // Absent on personalization (Door D) — no "savings vs singles" framing there.
      compareAtSubtotalCents: cfg.compareAtUnitCents ? qty * cfg.compareAtUnitCents : null
    };
  }

  function computeBox(cfg, qty) {
    // Quantity is whole boxes here, not cards. Total is the native box price x
    // boxes; the saving comes from the per-card rate vs buying loose singles.
    var cards = qty * cfg.blockSize;
    var subtotal = qty * cfg.boxPriceCents;
    var breakdown = [{
      label: qty + " box" + (qty > 1 ? "es" : "") + " of " + cfg.blockSize + " (" + cards + " cards)",
      amountCents: subtotal
    }];
    return {
      subtotal: subtotal,
      breakdown: breakdown,
      nudge: null,
      // Report cards so per-unit figures are per-card, not per-box.
      unitCount: cards,
      // Anchor the strikethrough on what those cards would cost as singles.
      compareAtSubtotalCents: cards * cfg.singleCents
    };
  }

  function fmt(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function computePricing(config, quantity) {
    var qty = Math.floor(Number(quantity) || 0);
    var minQty = config.minQty || 1;

    if (qty < minQty) {
      return {
        valid: false,
        validationMessage: "Minimum " + minQty + " card" + (minQty > 1 ? "s" : ""),
        quantity: qty,
        model: config.model,
        subtotalCents: null,
        unitEffectiveCents: null,
        compareAtSubtotalCents: null,
        compareAtUnitCents: null,
        savingsCents: null,
        discountPct: null,
        breakdown: [],
        nudge: null,
        minQty: minQty
      };
    }

    var out;
    if (config.model === "block_pricing") out = computeBlock(config, qty);
    else if (config.model === "volume_tiers") out = computeTiers(config, qty);
    else if (config.model === "box_multiple") out = computeBox(config, qty);
    else throw new Error("Unknown pricing model: " + config.model);

    // Per-unit figures divide by the card count, which equals qty for card-quantity
    // models and qty x blockSize for box_multiple (where qty is boxes).
    var unitCount = out.unitCount || qty;

    // Compare-at / discount: only when an anchor exists and actually beats the
    // live price. discountPct is floored so we never overstate the saving.
    var compareAt = out.compareAtSubtotalCents;
    var hasDiscount = (compareAt != null && compareAt > out.subtotal);
    var savings = hasDiscount ? compareAt - out.subtotal : null;
    var discountPct = hasDiscount ? Math.floor((savings / compareAt) * 100) : null;

    return {
      valid: true,
      validationMessage: null,
      quantity: qty,
      model: config.model,
      subtotalCents: out.subtotal,
      unitEffectiveCents: out.subtotal / unitCount,
      compareAtSubtotalCents: hasDiscount ? compareAt : null,
      compareAtUnitCents: hasDiscount ? compareAt / unitCount : null,
      savingsCents: savings,
      discountPct: discountPct,
      breakdown: out.breakdown,
      nudge: out.nudge,
      minQty: minQty
    };
  }

  /**
   * ROADMAP #66 (2026-07-29) — which door config does a line of `qty` get CHARGED at?
   * Structure + quantity alone; no inventory, no collection membership, no `_door`
   * property. A block door flips to the volume tiers (the tier door = volume_tiers
   * WITHOUT a setup fee — the setup-fee one is Personalize) once qty reaches the tier
   * floor. Anything else is returned unchanged.
   *
   * This is the CANONICAL copy of the rule. The Discount Function carries its own
   * hand-written mirror (Functions are self-contained and cannot import this file);
   * saturn-discount-parity.test.mjs asserts the two agree quantity-by-quantity, the
   * same way it guards targetSubtotalCents against computePricing. Change the rule
   * here and in the Function together, or the parity test will fail — which is the
   * point.
   *
   * NOT inventory-aware by design: the Function cannot read inventory, so if display
   * gated on stock while charge didn't, display != charge would reappear at the
   * boundary. Inventory gates what we ADVERTISE, never what we charge.
   */
  function resolveBillingDoor(config, pageCfg, qty) {
    if (!config || !config.doors || !pageCfg) return pageCfg;
    if (pageCfg.model !== "block_pricing") return pageCfg;
    for (var k in config.doors) {
      if (!Object.prototype.hasOwnProperty.call(config.doors, k)) continue;
      var d = config.doors[k];
      if (d && d.model === "volume_tiers" && !isPersonalizeDoor(d) && qty >= (d.minQty || 50)) return d;
    }
    return pageCfg;
  }

  /**
   * Is this tier door the PERSONALIZATION door (Door D)? Declared by
   * `personalized: true` on the config (ROADMAP #136, 2026-09-10 — the setup fee
   * was folded into the per-card rate, so fee PRESENCE stopped being the tell).
   * A legacy config that still carries `setupFeeCents` is Door D the old way, so
   * a store whose metafields have not been re-applied keeps resolving. The
   * Discount Function carries the same rule (isPersonalizeDoor) — parity-tested.
   */
  function isPersonalizeDoor(d) {
    return !!d && d.model === "volume_tiers" && (d.personalized === true || !!d.setupFeeCents);
  }

  var api = { computePricing: computePricing, formatCents: fmt, resolveBillingDoor: resolveBillingDoor, isPersonalizeDoor: isPersonalizeDoor };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (root) root.SaturnPricing = api;
})(typeof window !== "undefined" ? window : this);